import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useSyncExternalStore } from "react";
import { orderKeyAfterLast, type ListKind } from "@vmeste/shared";
import { operations } from "../lib/outbox.js";
import { positionForMove, positionsIn, selectLists } from "../lib/store.js";
import { syncEngine, type ConnectionStatus } from "../lib/sync.js";
import { toListViews, toMemberView } from "../lib/views.js";
import type { ListView, MemberView } from "./views.js";

/**
 * Lists, backed by the sync engine.
 *
 * Every mutation goes into the outbox and shows immediately; nothing here waits
 * for a round trip. What renders is synced state with unsent changes layered on
 * top, so a change stays visible until the server confirms it rather than
 * flickering back when the next delta arrives.
 */

interface ListsContextValue {
  listsFor(spaceId: string): ListView[];
  status: ConnectionStatus;
  /** Unsent changes, so the UI can say so honestly rather than pretend. */
  pending: number;
  createList(spaceId: string, kind: ListKind, title: string): void;
  addItem(spaceId: string, listId: string, text: string): void;
  toggleItem(spaceId: string, listId: string, itemId: string, userId: string): void;
  removeItem(spaceId: string, listId: string, itemId: string): void;
  /**
   * Deal with everything already ticked, in one gesture.
   *
   * `untick` is the weekly shop: the same list, with the marks taken off. Items
   * do not repeat (SPEC §4.2) and should not — a weekly shop is not a repeating
   * item, it is one list used again — but that decision only holds if clearing
   * the marks is a gesture rather than forty taps on a bin.
   *
   * `remove` is the other half, for a list of tasks where done means finished.
   *
   * One operation per row rather than a route of its own: they go through the
   * same queue as every other change, so they survive a dead connection, apply
   * optimistically and undo nothing that the other person did meanwhile. Fifty
   * small requests is the cost, and for two people with one shopping list that
   * is the right side of the trade.
   */
  clearDone(spaceId: string, listId: string, how: "untick" | "remove"): void;
  removeList(spaceId: string, listId: string): void;
  /**
   * Put a list out of the way, or take it back.
   *
   * The third answer the delete dialog could not give. It offered «отмена» or
   * «навсегда, у обоих», and the column this writes has existed since the first
   * migration — SPEC §4.2 asks for archiving *instead of* deleting, and nothing
   * on any screen ever wrote it.
   */
  archiveList(spaceId: string, listId: string, archived: boolean): void;
  /** Move an item to sit at `toIndex` among the open items of its list. */
  moveItem(spaceId: string, listId: string, itemId: string, toIndex: number): void;
}

const ListsContext = createContext<ListsContextValue | null>(null);

/*
 * The codec belongs to the space, so every write asks for the one that space is
 * read and written with (SPEC §3). `null` means it is encrypted and this device
 * has no key: the write is refused rather than falling back to plaintext, which
 * would put readable content into a space that is supposed to be encrypted.
 */

export function ListsProvider({ children }: { children: ReactNode }) {
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);

  const listsFor = useCallback(
    (spaceId: string): ListView[] => toListViews(syncEngine.view(spaceId)),
    // The engine notifies on every change, and useSyncExternalStore re-renders
    // this provider, so recomputing on each render is exactly right here.
    [snapshot],
  );

  const value = useMemo<ListsContextValue>(
    () => ({
      listsFor,
      status: snapshot.status,
      pending: snapshot.pending,

      createList(spaceId, kind, title) {
        const codec = syncEngine.codec(spaceId);
        if (!codec) return;

        void codec.encode({ title }).then((content) => {
          syncEngine.mutate(operations.createList(spaceId, kind, content));
        });
      },

      addItem(spaceId, listId, text) {
        const trimmed = text.trim();
        if (!trimmed) return;

        const codec = syncEngine.codec(spaceId);
        if (!codec) return;

        void codec.encode({ text: trimmed }).then((content) => {
          // Chosen here rather than before the encode: encoding is asynchronous,
          // and two quick additions that both read the state from before either
          // was queued came out with the same key. Reading it immediately before
          // the mutation means the previous one is already in the overlay.
          const position = orderKeyAfterLast(
            positionsIn(syncEngine.view(spaceId), listId),
          );

          syncEngine.mutate(
            operations.createItem(spaceId, { listId, content, position }),
          );
        });
      },

      toggleItem(spaceId, _listId, itemId) {
        const item = syncEngine.view(spaceId).items[itemId];
        if (!item) return;

        // `doneBy` and `doneAt` are the server's to set; sending them would be
        // a client claiming somebody else ticked the box.
        syncEngine.mutate(operations.updateItem(spaceId, itemId, { done: !item.done }));
      },

      removeItem(spaceId, _listId, itemId) {
        syncEngine.mutate(operations.deleteItem(spaceId, itemId));
      },

      clearDone(spaceId, listId, how) {
        const state = syncEngine.view(spaceId);
        const done = Object.values(state.items).filter(
          (item) => item.listId === listId && item.done && item.deletedAt === null,
        );

        for (const item of done) {
          syncEngine.mutate(
            how === "remove"
              ? operations.deleteItem(spaceId, item.id)
              : // `doneBy` and `doneAt` are the server's to clear, exactly as
                // they are the server's to set.
                operations.updateItem(spaceId, item.id, { done: false }),
          );
        }
      },

      removeList(spaceId, listId) {
        syncEngine.mutate(operations.deleteList(spaceId, listId));
      },

      archiveList(spaceId, listId, archived) {
        syncEngine.mutate(
          // The request carries a flag; the server stamps the time. A client
          // deciding when something was archived would be a client whose clock
          // matters.
          operations.updateList(spaceId, listId, { archived }),
        );
      },

      moveItem(spaceId, listId, itemId, toIndex) {
        const position = positionForMove(
          syncEngine.view(spaceId),
          listId,
          itemId,
          toIndex,
        );
        if (position === null) return;

        syncEngine.mutate(operations.updateItem(spaceId, itemId, { position }));
      },
    }),
    [listsFor, snapshot.status, snapshot.pending],
  );

  return <ListsContext.Provider value={value}>{children}</ListsContext.Provider>;
}

export function useLists(): ListsContextValue {
  const ctx = useContext(ListsContext);
  if (!ctx) throw new Error("useLists must be used inside <ListsProvider>");
  return ctx;
}

/** Members of a space, for naming whoever a task is assigned to. */
export function useMembers(spaceId: string | null): MemberView[] {
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);
  void snapshot;
  if (!spaceId) return [];
  return syncEngine.view(spaceId).members.map(toMemberView);
}

/** Whether a space has finished its first pull, for an honest empty state. */
export function useSpaceLoaded(spaceId: string | null): boolean {
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);
  void snapshot;
  if (!spaceId) return false;
  const state = syncEngine.view(spaceId);
  return state.revision > 0 || selectLists(state).length > 0;
}
